import { ArrowLeftIcon, ChevronDown, FilterIcon } from 'lucide-react';
import { Button } from '@ui/button.tsx';
import { Input } from '@ui/input.tsx';
import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@r/components/ui/collapsible.tsx';
import { cn } from '@r/utils/utils.ts';
import { useProductFilters } from '@r/hooks/use-product-filters.ts';
import { useShopBrands } from '@r/hooks/use-shop-brands.ts';
import { getProductCategoriesToFetch } from '@r/helpers/product-catalog.ts';
import { ProductBrandFilters } from '@client/components/shop/product-brand-filters.tsx';
import { RangeInput } from '@ui/range-input.tsx';
import { usePointsRangeFilter } from '@r/hooks/use-points-range-filter.ts';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetClose } from '@ui/sheet.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ui/select.tsx';
import { useSearch } from '@tanstack/react-router';
import type { ProductCategoryProductCategoryReadModel } from '@r/api/generated/client.schemas.ts';

type ProductCategory = ProductCategoryProductCategoryReadModel;

type ProductFiltersDrawerProps = {
  shopId: string;
  productCategories: ProductCategory[];
  pageSize: number;
  pageSizeOptions: number[];
  priceSort: 'asc' | 'desc';
  onPageSizeChange: (size: number) => void;
  onPriceSortChange: (sort: 'asc' | 'desc') => void;
};

const ProductFiltersDrawer = ({
  shopId,
  productCategories,
  pageSize,
  pageSizeOptions,
  priceSort,
  onPageSizeChange,
  onPriceSortChange
}: ProductFiltersDrawerProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const search = useSearch({ strict: false }) as {
    subcategoryId?: string | number;
    categoryId?: string | number;
    [key: string]: unknown;
  };

  const subcategoryId = search.subcategoryId ? String(search.subcategoryId) : undefined;
  const categoryId = search.categoryId ? String(search.categoryId) : undefined;
  const categoriesToFetch = useMemo(
    () => getProductCategoriesToFetch(subcategoryId, categoryId, productCategories),
    [subcategoryId, categoryId, productCategories]
  );

  const { data: brands = [], isSuccess: isBrandsSuccess } = useShopBrands({
    shopId,
    categoryIds: categoriesToFetch
  });
  const availableBrandIds = useMemo(() => brands.map((brand) => brand.id), [brands]);

  const {
    searchInput,
    setSearchInput,
    selectedCategoryId,
    selectedSubCategoryId,
    selectedBrandIds,
    openCategoryIds,
    setCategoryOpen,
    handleSelectCategory,
    handleSelectSubCategory,
    handleResetCategories,
    handleToggleBrand
  } = useProductFilters({
    productCategories,
    availableBrandIds: isBrandsSuccess ? availableBrandIds : undefined
  });

  const pointsRange = usePointsRangeFilter();

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        variant="default"
        size="icon"
        className="fixed right-5 bottom-5 z-50 rounded-full shadow-lg h-14 w-14 md:hidden"
      >
        <FilterIcon className="w-6 h-6" />
      </Button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="h-[85vh] flex flex-col">
          <SheetHeader>
            <SheetTitle className="text-2xl font-bold">{t('shop.filters.title')}</SheetTitle>
          </SheetHeader>

          <div className="mt-6 space-y-6 flex-1 overflow-y-auto pb-4">
            <div className="space-y-2">
              <h3 className="text-sm font-semibold">{t('label.search')}</h3>
              <Input
                className="rounded-full min-h-10"
                placeholder={t('label.search')}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                clear
              />
            </div>

            <div className="space-y-2">
              <h3 className="text-sm font-semibold">{t('pagination.limit')}</h3>
              <Select
                value={`${pageSize}`}
                onValueChange={(value) => {
                  onPageSizeChange(Number(value));
                }}
              >
                <SelectTrigger className="w-full rounded-full">
                  <SelectValue placeholder={pageSize} />
                </SelectTrigger>
                <SelectContent>
                  {pageSizeOptions.map((size) => (
                    <SelectItem key={size} value={`${size}`}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Price sort */}
            <div className="space-y-2">
              <h3 className="text-sm font-semibold">{t('pagination.sort.price')}</h3>
              <Select
                value={priceSort}
                onValueChange={(value: 'asc' | 'desc') => onPriceSortChange(value)}
              >
                <SelectTrigger className="w-full rounded-full">
                  <SelectValue placeholder={t('global.select')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="asc">{t('pagination.sort.price.asc')}</SelectItem>
                  <SelectItem value="desc">{t('pagination.sort.price.desc')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {productCategories.length > 0 && (
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <h3 className="text-sm font-semibold">{t('shop.filters.categories')}</h3>
                  {(selectedCategoryId !== null || selectedSubCategoryId !== null) && (
                    <Button
                      variant="link"
                      size="custom_sm"
                      className="px-0"
                      onClick={handleResetCategories}
                    >
                      <ArrowLeftIcon className="w-4 h-4 mr-1" />
                      {t('shop.product.sidebar.reset.categories')}
                    </Button>
                  )}
                </div>

                <div className="space-y-2">
                  {productCategories.map((category) =>
                    category.childs?.length ? (
                      <Collapsible
                        key={category.id}
                        className="w-full"
                        open={openCategoryIds.includes(category.id)}
                        onOpenChange={(open) => setCategoryOpen(category.id, open)}
                      >
                        <div className="flex items-center w-full min-w-0">
                          <button
                            type="button"
                            onClick={() => handleSelectCategory(category.id)}
                            className={cn(
                              'flex-1 py-2 text-sm font-bold text-left rounded transition-colors hover:bg-muted/30 whitespace-nowrap overflow-hidden text-ellipsis',
                              selectedCategoryId === category.id ? 'text-primary' : ''
                            )}
                            title={category.label}
                          >
                            {category.label}
                          </button>
                          <CollapsibleTrigger className="p-2 rounded transition-colors hover:bg-muted/50">
                            <ChevronDown className="h-4 w-4 transition-transform group-data-[state=open]:rotate-180 flex-shrink-0" />
                          </CollapsibleTrigger>
                        </div>

                        <CollapsibleContent>
                          <div className="pl-4 mt-2 space-y-1">
                            {category.childs.map((subCategory) => {
                              const isSelected = selectedSubCategoryId === subCategory.id;
                              return (
                                <button
                                  type="button"
                                  key={subCategory.id}
                                  onClick={() => handleSelectSubCategory(subCategory.id)}
                                  className={cn(
                                    'overflow-hidden py-2 w-full text-sm text-left whitespace-nowrap rounded hover:bg-muted/30 text-ellipsis',
                                    isSelected ? 'font-bold text-primary' : 'text-muted-foreground'
                                  )}
                                  title={subCategory.label}
                                >
                                  {subCategory.label}
                                </button>
                              );
                            })}
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    ) : (
                      <button
                        key={category.id}
                        type="button"
                        onClick={() => handleSelectCategory(category.id)}
                        className={cn(
                          'w-full py-2 text-sm font-bold text-left rounded transition-colors hover:bg-muted/30 whitespace-nowrap overflow-hidden text-ellipsis',
                          selectedCategoryId === category.id ? 'text-primary' : ''
                        )}
                        title={category.label}
                      >
                        {category.label}
                      </button>
                    )
                  )}
                </div>
              </div>
            )}

            <div className="pt-4 space-y-2 border-t">
              <div className="flex justify-between items-center">
                <h3 className="text-sm font-semibold">{t('shop.filters.points')}</h3>
                {pointsRange.hasPointsRange && (
                  <Button
                    variant="link"
                    size="custom_sm"
                    className="px-0"
                    onClick={pointsRange.handleResetPointsRange}
                  >
                    <ArrowLeftIcon className="w-4 h-4 mr-1" />
                    {t('shop.filters.points.reset')}
                  </Button>
                )}
              </div>
              <RangeInput
                minValue={pointsRange.minInput}
                maxValue={pointsRange.maxInput}
                onMinChange={pointsRange.setMinInput}
                onMaxChange={pointsRange.setMaxInput}
                minLabel={t('shop.filters.points.min')}
                maxLabel={t('shop.filters.points.max')}
                error={pointsRange.isInvalid ? t('shop.filters.points.invalid') : undefined}
                inputClassName="rounded-full"
              />
            </div>

            <ProductBrandFilters
              brands={brands}
              selectedBrandIds={selectedBrandIds}
              onToggleBrand={handleToggleBrand}
            />
          </div>

          <div className="flex-shrink-0 pt-4 pb-2 border-t">
            <SheetClose asChild>
              <Button className="w-full" size="lg" rounded="full">
                {t('shop.filters.apply')}
              </Button>
            </SheetClose>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export { ProductFiltersDrawer };
